<template lang="pug">
  .navbar__mobile
    .navbar__mobile__header
      .navbar__mobile__left
        navbar-hamburger(@open="openMenu", @close="closeMenu")
      slot(v-if="showLogo", name="logo")
      .navbar__mobile__right
        icon-button(
          :icon="searchIcon",
          :label="searchLabel",
          width="20px"
          height="20px",
          @click="toggleSearch"
        )
        .navbar__mobile__cart-wrapper
          navbar-cart
    .navbar__mobile__search(
      v-if="subnav === 'search'",
      @click.stop
    )
      .navbar__search-overlay--mobile
      navbar-search(
        ref="searchBox",
        v-model="searchParam",
        placeholder="Search Products",
        @open-results="showResults = true",
      )
      transition(name="fade")
        .navbar__mobile__search-results(v-if="showResults")
          navbar-search-results(
            :suggestions="suggestedSearches",
            :collection-handle="allProductsHandle",
            :search="searchParam",
            @selected="setSearch",
          )
    transition(name="fade", @after-enter="enableLinks()", @leave="disableLinks()")
      .navbar__mobile__nav(v-if="isMenuOpen && subnav !== 'search'")
        accordion-group
          component.navbar__mobile__linklist-wrapper(
            v-for="(link, index) in links",
            :is="link.children ? 'accordion' : 'navbar-mobile-link'",
            :key="link.title",
            :heading="link.title",
            :link="link.url",
            theme="header"
          )
            ul.navbar__mobile__linklist(v-if="link.children")
              li.navbar__mobile__link(v-for="childLink in link.children", :key="childLink.title")
                a(:href="childLink.url") {{ childLink.title }}
        .navbar__mobile__footer
          a.navbar__mobile__footer__link(href="/account")
            icon(name="user", width="21px" height="21px")
            span My Account
          a.navbar__mobile__footer__link(
            v-for="link in tertiaryLinks"
            :key="link.title"
            :href="link.url"
          ) {{ link.title }}

</template>

<script>
  import { mapState } from 'vuex';
  import { allProductsHandle } from 'scripts/config/collections.js';
  import { openChatWidget } from 'scripts/helpers/zendesk.js';

  import Icon from 'scripts/components/basic/Icon.vue';
  import Accordion from 'scripts/components/basic/Accordion.vue';
  import AccordionGroup from 'scripts/components/basic/AccordionGroup.vue';
  import PrimaryButton from 'scripts/components/buttons/PrimaryButton.vue';
  import NavbarCart from 'scripts/components/navigation/NavbarCart.vue';
  import NavbarHamburger from 'scripts/components/navigation/NavbarHamburger.vue';
  import NavbarMobileLink from 'scripts/components/navigation/NavbarMobileLink.vue';
  import NavbarSearch from 'scripts/components/navigation/NavbarSearch.vue';
  import NavbarSearchResults from 'scripts/components/navigation/NavbarSearchResults.vue';
  import IconButton from 'scripts/components/buttons/IconButton.vue';

  export default {
    name: 'NavbarMobile',
    components: {
      Icon,
      Accordion,
      AccordionGroup,
      NavbarCart,
      NavbarHamburger,
      NavbarMobileLink,
      NavbarSearch,
      NavbarSearchResults,
      PrimaryButton,
      IconButton,
    },
    props: {
      links: {
        type: Array,
        default: () => [],
      },
      tertiaryLinks: Array,
      loggedIn: {
        type: Boolean,
        default: false,
      },
      suggestedSearches: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        isMenuOpen: false,
        showLinks: false,
        subnav: null,
        showLogo: true,
        showSearch: false,
        showResults: false,
        searchParam: '',
        showSearchResults: false,
        allProductsHandle: allProductsHandle,
      };
    },
    computed: {
      ...mapState({
        scrollTop: state => state.scroll.scrollTop,
      }),
      userLink () {
        return (this.loggedIn) ? '/account' : '/account/login';
      },
      userLabel () {
        return (this.loggedIn) ? 'My Account' : 'Log In';
      },
      searchIcon () {
        return (this.showSearch) ? 'close' : 'search';
      },
      searchLabel () {
        return (this.showSearch) ? 'Close Search' : 'Open Search';
      },
    },
    methods: {
      openMenu () {
        this.isMenuOpen = true;
        this.closeSubnav();
      },
      closeMenu () {
        this.isMenuOpen = false;
        this.closeSubnav();
      },
      enableLinks () {
        this.showLinks = true;
      },
      disableLinks () {
        this.showLinks = false;
      },
      openSubnav (subnav) {
        this.subnav = subnav;
      },
      closeSubnav () {
        this.subnav = null;
        this.showResults = false;
        this.showSearch = false;
      },
      setSearch (val) {
        this.$refs.searchBox.update(val);
      },
      toggleSearch () {
        this.subnav = null;
        this.showSearch = !this.showSearch;

        if (this.showSearch) {
          this.subnav = 'search';
          if (this.searchParam !== '') this.showResults = true;
        } else if (!this.showSearch) {
          this.showResults = false;
          if (this.subnav === 'search') this.subnav = null;
        }
      },
      openHelp () {
        openChatWidget();
      },
    },
  };
</script>

<style scoped lang="scss">
  .navbar__mobile {
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    background-color: $bg--main;
    border-bottom: 1px solid $border--lightest;

    &__right {
      display: flex;
      justify-content: flex-end;
    }

    &__left {
      height: 25px;
    }

    &__left,
    &__right {
      width: calc((100% - #{$grid-gutter * 2} - 115px) / 2);
    }

    &::v-deep .navbar__logo {
      flex-grow: 0;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: $mobile-nav-height;
      padding: 9px 18px;
      background-color: $color-white;
      transition: 0.3s ease all;
    }

    &__cart-wrapper {
      display: flex;
      align-items: center;
      margin-left: 15px;
    }

    &__search {
      width: 100%;
      padding: 24px $grid-gutter;
      position: absolute;
      // include border
      top: $mobile-nav-height + 1px;
      background-color: $color-white;
      border-bottom: 1px solid $border--lightest;
    }

    &__search-results {
      position: absolute;
      top: calc(100% - 24px);
      left: $grid-gutter;
      width: calc(100% - #{$grid-gutter * 2});
      border: 1px solid $border--lightest;
      background-color: $bg--main;
      transition: 0.3s ease opacity;

      &:empty {
        opacity: 0;
      }
    }

    .navbar__search-results {
      display: block;
    }

    &__nav {
      display: flex;
      flex-direction: column;
      position: fixed;
      width: 100%;
      height: calc(100% - #{$mobile-nav-height});
      top: $mobile-nav-height;
      left: 0;
      overflow-y: auto;
      background-color: $bg--main;
      border-top: 1px solid $border--lightest;

      body.has-promobar & {
        height: calc(100% - #{$mobile-nav-height + $mobile-promobar-height});
        top: $mobile-nav-height + $mobile-promobar-height;
      }
    }

    &__linklist-wrapper {
      position: relative;

      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        left: $grid-gutter;
        height: 1px;
        width: calc(100% - #{$grid-gutter * 2});
        background-color: $border--lightest;
      }

      &::v-deep {
        .accordion__header {
          padding-top: 18px;
          padding-bottom: 18px;
        }
      }
    }

    &__linklist {
      list-style: none;
      margin: 0;
      padding: 0 0 0 9px;
    }

    &__link {
      font-size: rem(16);
      line-height: 1.4375;
      color: $color-indigo;
      font-family: $font-gibson;

      &:not(:last-child) {
        margin-bottom: 27px;
      }
    }

    &__footer {
      padding: 25px $grid-gutter;
      display: flex;
      flex-direction: column;

      &__link {
        margin-top: $grid-gutter;
        font-size: rem(14);
        font-weight: 700;
        color: $color-indigo;

        &::v-deep .icon {
          margin-right: 8px;
          vertical-align: bottom;
        }

        &:not(:last-child) {
          margin-right: 27px;
        }

        span {
          line-height: 21px;
          height: 21px;
          display: inline-block;
        }
      }
    }

    .accordion__header {
      color: $color-indigo;
      text-transform: uppercase;
    }
  }

  ::v-deep .accordion--header svg {
    fill: $color-indigo;
  }

  .navbar__search-overlay--mobile {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    z-index: -49;
    background-color: $bg--overlay;
  }
</style>
